"use client";

import React, { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface AuthSwitchProps {
  defaultMode?: "login" | "register";
  loginForm: React.ReactNode;
  registerForm: React.ReactNode;
  loginTitle?: string;
  loginDescription?: string;
  loginSwitchLabel?: string;
  registerTitle?: string;
  registerDescription?: string;
  registerSwitchLabel?: string;
}

const AuthSwitch = ({
  defaultMode = "login",
  loginForm,
  registerForm,
  loginTitle = "Nie masz konta?",
  loginDescription = "Dołącz do nas i odkryj świat viralowych treści. Stwórz konto w kilka sekund!",
  loginSwitchLabel = "Zarejestruj się",
  registerTitle = "Masz już konto?",
  registerDescription = "Zaloguj się, aby kontynuować tworzenie viralowych treści.",
  registerSwitchLabel = "Zaloguj się",
}: AuthSwitchProps) => {
  const [isSignUp, setIsSignUp] = useState(defaultMode === "register");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (isSignUp) container.classList.add("sign-up-mode");
    else container.classList.remove("sign-up-mode");
  }, [isSignUp]);

  return (
    <>
      <style>{`
        .auth-container {
          position: relative;
          width: 100%;
          max-width: 1150px;
          min-height: 620px;
          background-color: hsl(var(--card));
          border-radius: 1.25rem;
          overflow: hidden;
          box-shadow: var(--shadow-card);
          border: 1px solid hsl(var(--border) / 0.5);
        }

        .auth-container::before {
          content: "";
          position: absolute;
          height: 2000px;
          width: 2000px;
          top: -10%;
          right: 48%;
          transform: translateY(-50%);
          background: linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-glow)));
          transition: 1.8s ease-in-out;
          border-radius: 50%;
          z-index: 6;
        }

        .auth-container.sign-up-mode::before {
          transform: translate(100%, -50%);
          right: 52%;
        }

        .forms-container {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        }

        .signin-signup {
          position: absolute;
          top: 50%;
          transform: translate(-50%, -50%);
          left: 75%;
          width: 42%;
          max-width: 380px; /* 30% smaller */
          transition: 1s 0.7s ease-in-out;
          display: grid;
          grid-template-columns: 1fr;
          z-index: 5;
          padding: 0 1rem;
        }

        .auth-container.sign-up-mode .signin-signup {
          left: 25%;
        }

        .auth-form {
          display: flex;
          align-items: stretch;
          justify-content: center;
          flex-direction: column;
          overflow: visible;
          grid-column: 1 / 2;
          grid-row: 1 / 2;
          transition: all 0.2s 0.7s;
          opacity: 1;
          z-index: 2;
          width: 100%;
        }

        .auth-form.sign-up-form {
          opacity: 0;
          z-index: 1;
        }

        .auth-container.sign-up-mode .auth-form.sign-in-form {
          opacity: 0;
          z-index: 1;
        }

        .auth-container.sign-up-mode .auth-form.sign-up-form {
          opacity: 1;
          z-index: 2;
        }

        .panels-container {
          position: absolute;
          height: 100%;
          width: 100%;
          top: 0;
          left: 0;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
        }

        .auth-panel {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          justify-content: space-around;
          text-align: center;
          z-index: 6;
        }

        .left-panel {
          pointer-events: all;
          padding: 3rem 12% 2rem 12%;
        }

        .right-panel {
          pointer-events: none;
          padding: 3rem 12% 2rem 12%;
        }

        .auth-panel .panel-content {
          color: hsl(var(--primary-foreground));
          transition: transform 0.9s ease-in-out;
          transition-delay: 0.6s;
        }

        .auth-panel .panel-content h3 {
          font-weight: 700;
          line-height: 1.2;
          font-size: 1.5rem;
          margin-bottom: 0.75rem;
        }

        .auth-panel .panel-content p {
          font-size: 0.875rem;
          padding: 0.5rem 0;
          opacity: 0.9;
          line-height: 1.5;
        }

        .auth-container.sign-up-mode .left-panel .panel-content {
          transform: translateX(-800px);
        }

        .auth-container.sign-up-mode .left-panel {
          pointer-events: none;
        }

        .auth-container.sign-up-mode .right-panel {
          pointer-events: all;
        }

        .right-panel .panel-content {
          transform: translateX(800px);
        }

        .auth-container.sign-up-mode .right-panel .panel-content {
          transform: translateX(0);
        }

        .panel-btn {
          margin-top: 1rem;
          padding: 0.625rem 1.75rem;
          background-color: transparent;
          border: 2px solid hsl(var(--primary-foreground));
          color: hsl(var(--primary-foreground));
          font-size: 0.875rem;
          font-weight: 600;
          border-radius: 0.75rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .panel-btn:hover {
          background-color: hsl(var(--primary-foreground));
          color: hsl(var(--primary));
        }

        /* Mobile responsive */
        @media (max-width: 870px) {
          .auth-container {
            min-height: auto;
            height: auto;
            border-radius: 1rem;
          }

          .auth-container::before {
            display: none;
          }

          .forms-container {
            position: relative;
          }

          .signin-signup {
            position: relative;
            top: auto;
            left: auto !important;
            transform: none;
            width: 100%;
            padding: 2rem 1.5rem;
          }

          .panels-container {
            display: none;
          }

          .auth-form {
            opacity: 1 !important;
            z-index: 2 !important;
          }

          .auth-form.sign-up-form {
            display: none;
          }

          .auth-container.sign-up-mode .auth-form.sign-in-form {
            display: none;
          }

          .auth-container.sign-up-mode .auth-form.sign-up-form {
            display: flex;
            opacity: 1 !important;
            z-index: 2 !important;
          }
        }
      `}</style>

      <div className="auth-container" ref={containerRef}>
        <div className="forms-container">
          <div className="signin-signup">
            {/* Sign In Form */}
            <div className="auth-form sign-in-form w-full">
              {loginForm}
            </div>

            {/* Sign Up Form */}
            <div className="auth-form sign-up-form w-full">
              {registerForm}
            </div>
          </div>
        </div>

        <div className="panels-container">
          <div className="auth-panel left-panel">
            <div className="panel-content">
              <h3>{loginTitle}</h3>
              <p>{loginDescription}</p>
              <button
                className="panel-btn"
                onClick={() => setIsSignUp(true)}
              >
                {loginSwitchLabel}
              </button>
            </div>
          </div>

          <div className="auth-panel right-panel">
            <div className="panel-content">
              <h3>{registerTitle}</h3>
              <p>{registerDescription}</p>
              <button
                className="panel-btn"
                onClick={() => setIsSignUp(false)}
              >
                {registerSwitchLabel}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile toggle - shown only on small screens */}
      <div className="mt-4 text-center block md:hidden">
        <button
          onClick={() => setIsSignUp(!isSignUp)}
          className="text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          {isSignUp ? "Masz już konto? " : "Nie masz konta? "}
          <span className="font-semibold text-primary">
            {isSignUp ? "Zaloguj się" : "Zarejestruj się"}
          </span>
        </button>
      </div>
    </>
  );
};

export { AuthSwitch };
